import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { MarkdownRenderer } from '../../components/Chat/MarkdownRenderer';
import { useEditorStore } from '../../store/editorStore';
import { useForegroundTiming } from '../../hooks/useForegroundTiming';
import { FAQ_CATEGORIES, FAQ_TOTAL_QUESTIONS } from '../../data/rStatsFaq';
import { hasAttachableWorkspace, buildTutorSystemPrompt, wrapCuratedQuestion } from '../../utils/aiTutor';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ModalOverlay = styled.div<{ $visible: boolean }>`
  display: ${props => props.$visible ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 10000;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: rgba(30, 30, 30, 0.98);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  width: 90vw;
  max-width: 1200px;
  height: 85vh;
  max-height: 85vh;
  display: flex;
  flex-direction: row;
  animation: slideIn 0.3s ease;
  overflow: hidden;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ChatSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  min-width: 0;
`;

const FAQSection = styled.div`
  width: 260px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: rgba(102, 126, 234, 0.05);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(102, 126, 234, 0.1);
`;

const ModalTitle = styled.h2`
  color: #ffd700;
  margin: 0;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &:before {
    content: '💡';
    font-size: 1.8rem;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(248, 113, 113, 0.3);
    transform: rotate(90deg);
  }
`;

const StopButton = styled.button`
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid rgba(239, 68, 68, 0.5);
  border-radius: 20px;
  padding: 0.4rem 0.8rem;
  color: #fca5a5;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.4rem;

  &:hover {
    background: rgba(239, 68, 68, 0.3);
    border-color: rgba(239, 68, 68, 0.7);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  &:before {
    content: '⏹';
    font-size: 0.9rem;
  }
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.5);
    border-radius: 10px;

    &:hover {
      background: rgba(102, 126, 234, 0.7);
    }
  }
`;

const MessageBubble = styled.div<{ $isUser: boolean }>`
 
  max-width: 100%;
  padding: 1rem 1.2rem;
  border-radius: 18px;
  line-height: 1.5;
  word-wrap: break-word;
  align-self: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
  background: ${props => props.$isUser
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$isUser ? 'white' : 'rgba(255, 255, 255, 0.9)'};
  border: ${props => props.$isUser ? 'none' : '1px solid rgba(255, 255, 255, 0.2)'};
  animation: messageSlideIn 0.3s ease;

  @keyframes messageSlideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  code {
    background: rgba(0, 0, 0, 0.3);
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
    color: #4ade80;
  }

  pre {
    background: rgba(0, 0, 0, 0.4);
    padding: 1rem;
    border-radius: 8px;
    overflow-x: auto;
    margin: 0.5rem 0;

    code {
      background: none;
      padding: 0;
      color: #d4d4d4;
    }
  }

  p {
    margin: 0.5rem 0;
  }

  ul, ol {
    margin-left: 1.5rem;
    margin: 0.5rem 0;
  }

  li {
    margin: 0.3rem 0;
  }

  strong {
    color: #ffd700;
    font-weight: 600;
  }
`;

const ChatInputSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(102, 126, 234, 0.05);
`;

const InputWrapper = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
`;

const ChatInput = styled.textarea`
  flex: 1;
  padding: 0.8rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  color: white;
  font-size: 0.95rem;
  font-family: inherit;
  resize: none;
  min-height: 50px;
  max-height: 120px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    background: rgba(255, 255, 255, 0.15);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.5);
    border-radius: 10px;
  }
`;

const SendButton = styled.button<{ $disabled?: boolean }>`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 15px;
  width: 50px;
  height: 50px;
  color: white;
  font-size: 1.2rem;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:hover {
    transform: ${props => props.$disabled ? 'none' : 'scale(1.05)'};
    box-shadow: ${props => props.$disabled ? 'none' : '0 5px 15px rgba(102, 126, 234, 0.4)'};
  }
`;

const ExplainCodeButton = styled.button<{ $disabled?: boolean }>`
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  border: none;
  border-radius: 12px;
  padding: 0.5rem 1rem;
  color: white;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:hover {
    transform: ${props => props.$disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$disabled ? 'none' : '0 5px 15px rgba(240, 147, 251, 0.4)'};
  }

  &:before {
    content: '🔍';
    font-size: 0.9rem;
  }
`;

const FAQHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(102, 126, 234, 0.1);
`;

const FAQTitle = styled.h3`
  color: #ffd700;
  margin: 0;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;

  &:before {
    content: '💡';
    font-size: 1.2rem;
  }
`;

const FAQList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.5);
    border-radius: 10px;

    &:hover {
      background: rgba(102, 126, 234, 0.7);
    }
  }
`;

const FAQCategory = styled.div`
  margin-bottom: 0.5rem;
`;

const CategoryToggle = styled.button<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  text-align: left;
  background: ${props => props.$expanded ? 'rgba(102, 126, 234, 0.18)' : 'rgba(255, 255, 255, 0.04)'};
  border: 1px solid ${props => props.$expanded ? 'rgba(102, 126, 234, 0.45)' : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 8px;
  color: #8fa3f5;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 0.5rem 0.6rem;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(102, 126, 234, 0.25);
    border-color: rgba(102, 126, 234, 0.5);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.6);
  }
`;

const CategoryChevron = styled.span<{ $expanded: boolean }>`
  flex-shrink: 0;
  font-size: 0.65rem;
  line-height: 1;
  transition: transform 0.2s ease;
  transform: rotate(${props => props.$expanded ? '90deg' : '0deg'});
`;

const CategoryLabel = styled.span`
  flex: 1;
  min-width: 0;
`;

const CategoryCount = styled.span`
  flex-shrink: 0;
  font-size: 0.65rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.55);
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  padding: 0.05rem 0.4rem;
`;

const FaqSearchBar = styled.div`
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const FaqSearchInput = styled.input`
  width: 100%;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(0, 0, 0, 0.3);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.75rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.6);
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.25);
  }
`;

const FaqSearchHint = styled.div`
  margin-top: 0.35rem;
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.55);
`;

const FAQQuestionList = styled.div`
  padding: 0.4rem 0 0.2rem 0.3rem;
`;

const ContextChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  align-self: flex-start;
  margin-bottom: 0.5rem;
  padding: 0.2rem 0.55rem;
  border-radius: 12px;
  background: rgba(102, 126, 234, 0.15);
  border: 1px solid rgba(102, 126, 234, 0.35);
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.7rem;
  cursor: default;
`;

const FAQItem = styled.button`
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.4rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.72rem;
  cursor: pointer;
  transition: all 0.3s ease;
  line-height: 1.4;
  white-space: normal;
  overflow-wrap: break-word;

  &:hover {
    background: rgba(102, 126, 234, 0.2);
    border-color: rgba(102, 126, 234, 0.4);
    transform: translateX(5px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.6);
  }

  &:before {
    content: '❓';
    margin-right: 0.3rem;
    font-size: 0.75rem;
    flex-shrink: 0;
  }
`;

const LoadingSpinner = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 2px solid #667eea;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const StreamingCursor = styled.span`
  display: inline-block;
  margin-left: 2px;
  color: #667eea;
  animation: blink 1s infinite;
  font-weight: bold;

  @keyframes blink {
    0%, 49% { opacity: 1; }
    50%, 100% { opacity: 0; }
  }
`;

interface Message {
  content: string;
  isUser: boolean;
  timestamp: Date;
  isStreaming?: boolean;
  savedToBackend?: boolean;
}

interface AIHelpModalProps {
  visible: boolean;
  onClose: () => void;
  currentCode?: string;
}


export const AIHelpModal: React.FC<AIHelpModalProps> = ({ visible, onClose, currentCode = '' }) => {
  useForegroundTiming(visible ? {context: 'platform', platformKey: 'workspace', surface: 'aiHelp'} : null, visible, null, false, 10);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<number[]>([0]);
  const [faqQuery, setFaqQuery] = useState('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentExplanationIdRef = useRef<string | null>(null); // Track current explanation session ID

  // Re-renders only when the boolean flips, not on every keystroke in the editor
  const workspaceAttached = useEditorStore(state => hasAttachableWorkspace(state.code, state.error));

  const toggleCategory = (index: number) => {
    setExpandedCategories(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  // With ~160 questions an accordion alone is not navigable, so searching filters
  // questions across every chapter. A chapter title match keeps all its questions.
  const normalizedQuery = faqQuery.trim().toLowerCase();
  const visibleCategories = FAQ_CATEGORIES
    .map((category, originalIndex) => {
      if (!normalizedQuery) {
        return { ...category, originalIndex };
      }
      const titleMatches = category.title.toLowerCase().includes(normalizedQuery);
      const questions = titleMatches
        ? category.questions
        : category.questions.filter(q => q.toLowerCase().includes(normalizedQuery));
      return { ...category, questions, originalIndex };
    })
    .filter(category => category.questions.length > 0);

  const matchCount = visibleCategories.reduce((sum, c) => sum + c.questions.length, 0);

  // Initialize welcome message when modal opens
  useEffect(() => {
    if (visible && messages.length === 0) {
      setMessages([{
        content: '你好！我是R语言编程助手。我可以帮你解答关于R语言编程、统计分析和数据可视化的问题。\n\n你可以直接提问，或者点击右侧的常见问题快速获取答案。',
        isUser: false,
        timestamp: new Date()
      }]);
      // Reset explanation ID for new session
      currentExplanationIdRef.current = null;
      console.log('[CODE EXPLANATION] Starting new session, reset explanation ID');
    }
  }, [visible, messages.length]);

  // Save all conversations to backend when streaming completes
  useEffect(() => {
    // Only save if modal is visible and there's actual conversation (beyond welcome message)
    if (!visible || messages.length <= 1) {
      return;
    }

    // Find all unsaved assistant messages and their preceding user messages
    const pairsToSave: Array<{ userMsg: Message; assistantMsg: Message; assistantIndex: number }> = [];

    for (let i = 1; i < messages.length; i++) {
      const prevMsg = messages[i - 1];
      const currentMsg = messages[i];

      // Look for user+assistant pairs where the assistant message is complete (not streaming)
      // and NEITHER message has been successfully saved yet
      const isUserMessage = prevMsg?.isUser && prevMsg.content;
      const isAssistantMessage = !currentMsg.isUser && currentMsg.content && !currentMsg.isStreaming;
      const notYetSaved = !prevMsg.savedToBackend && !currentMsg.savedToBackend;

      if (isUserMessage && isAssistantMessage && notYetSaved) {
        pairsToSave.push({
          userMsg: prevMsg,
          assistantMsg: currentMsg,
          assistantIndex: i
        });
      }
    }

    if (pairsToSave.length === 0) {
      return;
    }

    // Save all pairs to backend in sequence (not parallel to maintain order)
    const saveAllPairs = async () => {
      for (const { userMsg, assistantMsg, assistantIndex } of pairsToSave) {
        // Determine if this is a code explanation or general Q&A
        const isCodeExplanation = currentCode && (
          userMsg.content.includes('解释以下R代码') ||
          userMsg.content.includes('```r') ||
          userMsg.content.includes('代码')
        );

        try {
          // Save to backend
          if (isCodeExplanation) {
            await saveCodeExplanation(currentCode || '', [userMsg, assistantMsg]);
          } else {
            await saveCodeExplanation('', [userMsg, assistantMsg]);
          }

          // Only mark as saved AFTER successful save
          setMessages(prev => {
            const newMessages = [...prev];
            // Mark both user and assistant messages as saved
            newMessages[assistantIndex - 1] = { ...newMessages[assistantIndex - 1], savedToBackend: true };
            newMessages[assistantIndex] = { ...newMessages[assistantIndex], savedToBackend: true };
            return newMessages;
          });
        } catch (error) {
          // Don't mark as saved, so it will be retried on the next effect run
        }
      }
    };

    saveAllPairs().catch(() => {});
  }, [messages, visible, currentCode]);

  // Handle ESC key to close modal and cleanup abort controller
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (visible && e.key === 'Escape') {
        // Abort any ongoing request before closing
        abortControllerRef.current?.abort();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      // Abort any ongoing request when component unmounts
      abortControllerRef.current?.abort();
    };
  }, [visible, onClose]);

  // Cleanup when modal closes
  useEffect(() => {
    if (!visible) {
      // Abort any ongoing request when modal closes
      abortControllerRef.current?.abort();
    }
  }, [visible]);

  // Helper function to save code explanation to backend
  const saveCodeExplanation = async (code: string, messagesToSave: Message[]) => {
    try {
      // Get token from auth-storage (used by the rest of the app)
      let token = null;
      const authStorage = localStorage.getItem('auth-storage');
      if (authStorage) {
        try {
          const parsed = JSON.parse(authStorage);
          token = parsed.state?.token || null;
        } catch (e) {
          // ignore parse errors
        }
      }

      const currentExplanationId = currentExplanationIdRef.current;

      let response;
      const messagesPayload = messagesToSave.map(msg => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.content,
        timestamp: msg.timestamp
      }));

      if (currentExplanationId) {
        // Update existing session - append new messages
        response = await fetch(`${API_BASE_URL}/api/code-explanations/explanations/${currentExplanationId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({
            messages: messagesPayload
          })
        });
      } else {
        // Create new session
        response = await fetch(`${API_BASE_URL}/api/code-explanations/explanations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({
            code,
            language: 'r',
            messages: messagesPayload,
            llmProvider: 'deepseek',
            llmModel: 'deepseek-chat',
            sessionId: localStorage.getItem('sessionId') || null
          })
        });
      }

      if (response.ok) {
        const result = await response.json();

        // Store the explanation ID for future updates
        if (!currentExplanationId) {
          currentExplanationIdRef.current = result.data._id;
        }
      } else {
        console.error('Failed to save code explanation:', response.status);
      }
    } catch (error) {
      console.error('Error saving code explanation:', error);
    }
  };

  const callDeepSeekAPI = async (
    userMessage: string,
    messageIndex: number,
    onChunk: (chunk: string) => void,
    onComplete: () => void,
    onError: (error: Error) => void
  ): Promise<void> => {
    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Get API key from localStorage (same as chatStore)
    const apiKey = localStorage.getItem('deepseekApiKey') || process.env.REACT_APP_DEEPSEEK_API_KEY || '';
    const baseUrl = 'https://api.deepseek.com/v1';
    const model = 'deepseek-chat';

    // Build conversation history from messages
    const conversationMessages = [
      {
        role: 'system',
        content: buildTutorSystemPrompt()
      },
      // Include previous messages for context (last 10 messages)
      ...messages.slice(-10).map(msg => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.content
      })),
      {
        role: 'user',
        content: userMessage
      }
    ];

    try {
      if (!apiKey) {
        throw new Error('请先配置LLM API密钥');
      }
      const response = await fetch(`${baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model,
          messages: conversationMessages,
          max_tokens: 8000,
          temperature: 0.7,
          stream: true // Enable streaming
        }),
        signal: abortController.signal // Add abort signal
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      // Read the stream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          onComplete();
          break;
        }

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages (each line starts with "data: ")
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          const trimmedLine = line.trim();

          if (trimmedLine === 'data: [DONE]') {
            onComplete();
            return;
          }

          if (trimmedLine.startsWith('data: ')) {
            try {
              const jsonStr = trimmedLine.slice(6); // Remove "data: " prefix
              const data = JSON.parse(jsonStr);

              // Extract the delta content
              const delta = data.choices?.[0]?.delta;
              if (delta?.content) {
                onChunk(delta.content);
              }
            } catch (e) {
              // Skip invalid JSON lines
              console.warn('Failed to parse SSE data:', trimmedLine);
            }
          }
        }
      }
    } catch (error) {
      // Check if it was an abort error
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Stream aborted by user');
        return; // Don't call onError for abort
      }
      onError(error as Error);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      content: input,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const userQuestion = input;
    setInput('');
    setIsLoading(true);

    // Add empty AI message first
    const aiMessageIndex = messages.length + 1;
    setMessages(prev => [...prev, {
      content: '',
      isUser: false,
      timestamp: new Date(),
      isStreaming: true
    }]);

    // Start streaming
    await callDeepSeekAPI(
      userQuestion,
      aiMessageIndex,
      // onChunk - append each token to the message
      (chunk: string) => {
        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[aiMessageIndex] = {
            ...newMessages[aiMessageIndex],
            content: newMessages[aiMessageIndex].content + chunk,
            isStreaming: true
          };
          return newMessages;
        });
      },
      // onComplete - mark streaming as complete
      () => {
        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[aiMessageIndex] = {
            ...newMessages[aiMessageIndex],
            isStreaming: false
          };
          return newMessages;
        });
        setIsLoading(false);
      },
      // onError - handle errors
      (error: Error) => {
        console.error('AI API call failed:', error);
        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[aiMessageIndex] = {
            content: '抱歉，我遇到了一些问题。请检查：\n1. 后端服务器是否正在运行\n2. DeepSeek API密钥是否配置正确\n3. 网络连接是否正常\n\n你可以稍后再试，或者联系技术支持。',
            isUser: false,
            timestamp: new Date(),
            isStreaming: false
          };
          return newMessages;
        });
        setIsLoading(false);
      }
    );
  };

  const handleFAQClick = async (question: string) => {
    const userMessage: Message = {
      content: question,
      isUser: true,
      timestamp: new Date()
    };

    // FAQ entries are terse "如何…？" prompts, which bias the model toward a bare
    // code recipe. The chat still shows the clean question; only the model sees this.
    const teachingRequest = wrapCuratedQuestion(question);

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Add empty AI message first
    const aiMessageIndex = messages.length + 1;
    setMessages(prev => [...prev, {
      content: '',
      isUser: false,
      timestamp: new Date(),
      isStreaming: true
    }]);

    // Start streaming
    await callDeepSeekAPI(
      teachingRequest,
      aiMessageIndex,
      // onChunk - append each token to the message
      (chunk: string) => {
        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[aiMessageIndex] = {
            ...newMessages[aiMessageIndex],
            content: newMessages[aiMessageIndex].content + chunk,
            isStreaming: true
          };
          return newMessages;
        });
      },
      // onComplete - mark streaming as complete
      () => {
        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[aiMessageIndex] = {
            ...newMessages[aiMessageIndex],
            isStreaming: false
          };
          return newMessages;
        });
        setIsLoading(false);
      },
      // onError - handle errors
      (error: Error) => {
        console.error('AI API call failed:', error);
        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[aiMessageIndex] = {
            content: '抱歉，我遇到了一些问题。请稍后再试。',
            isUser: false,
            timestamp: new Date(),
            isStreaming: false
          };
          return newMessages;
        });
        setIsLoading(false);
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleExplainCode = async () => {
    if (!currentCode.trim() || isLoading) return;

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const codeMessage: Message = {
      content: `请帮我解释以下R代码的每一行：\n\n\`\`\`r\n${currentCode}\n\`\`\``,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, codeMessage]);
    setIsLoading(true);

    // Add empty AI message first
    const aiMessageIndex = messages.length + 1;
    setMessages(prev => [...prev, {
      content: '',
      isUser: false,
      timestamp: new Date(),
      isStreaming: true
    }]);

    // Use a specialized prompt for code explanation
    const explanationPrompt = `你是一个专业的R语言代码解释助手。请详细解释以下R代码的每一行，包括：
1. 代码的功能和作用
2. 每行代码的具体含义
3. 使用的函数和参数
4. 输出结果的解释
5. 可能的改进建议

代码：
\`\`\`r
${currentCode}
\`\`\`

请用简洁清晰的中文回答，使用Markdown格式。`;

    const apiKey = localStorage.getItem('deepseekApiKey') || process.env.REACT_APP_DEEPSEEK_API_KEY || '';

    try {
      if (!apiKey) {
        throw new Error('请先配置LLM API密钥');
      }
      const response = await fetch('https://api.deepseek.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: '你是一个专业的R语言代码解释助手，擅长用简洁清晰的中文解释R代码的每一行，帮助用户理解代码的功能和逻辑。'
            },
            {
              role: 'user',
              content: explanationPrompt
            }
          ],
          max_tokens: 8000,
          temperature: 0.7,
          stream: true // Enable streaming
        }),
        signal: abortController.signal // Add abort signal
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      // Read the stream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          setMessages(prev => {
            const newMessages = [...prev];
            newMessages[aiMessageIndex] = {
              ...newMessages[aiMessageIndex],
              isStreaming: false
            };
            return newMessages;
          });
          setIsLoading(false);
          break;
        }

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages (each line starts with "data: ")
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          const trimmedLine = line.trim();

          if (trimmedLine === 'data: [DONE]') {
            setMessages(prev => {
              const newMessages = [...prev];
              newMessages[aiMessageIndex] = {
                ...newMessages[aiMessageIndex],
                isStreaming: false
              };
              return newMessages;
            });
            setIsLoading(false);
            return;
          }

          if (trimmedLine.startsWith('data: ')) {
            try {
              const jsonStr = trimmedLine.slice(6); // Remove "data: " prefix
              const data = JSON.parse(jsonStr);

              // Extract the delta content
              const delta = data.choices?.[0]?.delta;
              if (delta?.content) {
                setMessages(prev => {
                  const newMessages = [...prev];
                  newMessages[aiMessageIndex] = {
                    ...newMessages[aiMessageIndex],
                    content: newMessages[aiMessageIndex].content + delta.content,
                    isStreaming: true
                  };
                  return newMessages;
                });
              }
            } catch (e) {
              // Skip invalid JSON lines
              console.warn('Failed to parse SSE data:', trimmedLine);
            }
          }
        }
      }
    } catch (error) {
      console.error('Code explanation failed:', error);

      // Check if it was an abort error
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Code explanation aborted by user');
        // Don't overwrite the partial content - streaming is already stopped
        return;
      }

      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[aiMessageIndex] = {
          content: '抱歉，代码解释失败。请检查网络连接和API配置。',
          isUser: false,
          timestamp: new Date(),
          isStreaming: false
        };
        return newMessages;
      });
      setIsLoading(false);
    }
  };

  return createPortal(
    <ModalOverlay $visible={visible} onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ChatSection>
          <ModalHeader>
            <ModalTitle>代码解释</ModalTitle>
            <CloseButton onClick={onClose} title="关闭 (ESC)">
              ×
            </CloseButton>
          </ModalHeader>

          <ChatMessages>
            {messages.map((message, index) => (
              <MessageBubble key={index} $isUser={message.isUser}>
                <MarkdownRenderer content={message.content} />
                {message.isStreaming && <StreamingCursor />}
              </MessageBubble>
            ))}
            {isLoading && !messages.some(m => m.isStreaming) && (
              <MessageBubble $isUser={false}>
                <LoadingSpinner />
              </MessageBubble>
            )}
          </ChatMessages>

          <ChatInputSection>
            {workspaceAttached && (
              <ContextChip title="提问时会自动附带你编辑器中的代码和最近一次运行结果，帮助AI更准确地回答">
                📎 已附带当前代码与运行结果
              </ContextChip>
            )}
            <InputWrapper>
              <ChatInput
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="输入你的R语言问题... (Shift+Enter 换行)"
                disabled={isLoading}
              />
              {isLoading && messages.some(m => m.isStreaming) ? (
                <StopButton
                  onClick={() => {
                    abortControllerRef.current?.abort();
                    setMessages(prev => {
                      const newMessages = [...prev];
                      const streamingIndex = newMessages.findIndex(m => m.isStreaming);
                      if (streamingIndex >= 0) {
                        newMessages[streamingIndex] = {
                          ...newMessages[streamingIndex],
                          isStreaming: false
                        };
                      }
                      return newMessages;
                    });
                    setIsLoading(false);
                  }}
                  title="停止生成"
                >
                  停止
                </StopButton>
              ) : (
                <SendButton
                  onClick={handleSend}
                  $disabled={!input.trim() || isLoading}
                  title="发送"
                >
                  {isLoading ? <LoadingSpinner /> : '📤'}
                </SendButton>
              )}
            </InputWrapper>
          </ChatInputSection>
        </ChatSection>

        <FAQSection>
          <FAQHeader>
            <FAQTitle>常见问题</FAQTitle>
          </FAQHeader>
          {currentCode.trim() && (
            <div style={{ padding: '1rem', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', background: 'rgba(102, 126, 234, 0.1)' }}>
              <ExplainCodeButton
                onClick={handleExplainCode}
                $disabled={isLoading}
                style={{ width: '100%' }}
              >
                解释当前代码
              </ExplainCodeButton>
            </div>
          )}
          <FaqSearchBar>
            <FaqSearchInput
              type="search"
              value={faqQuery}
              onChange={e => setFaqQuery(e.target.value)}
              placeholder={`搜索 ${FAQ_TOTAL_QUESTIONS} 个问题…`}
              aria-label="搜索常见问题"
            />
            {normalizedQuery && (
              <FaqSearchHint>
                {matchCount > 0 ? `找到 ${matchCount} 个相关问题` : '未找到相关问题，试试其他关键词'}
              </FaqSearchHint>
            )}
          </FaqSearchBar>
          <FAQList>
            {visibleCategories.map(category => {
              const categoryIndex = category.originalIndex;
              // While searching, results stay open so matches are immediately visible
              const isExpanded = normalizedQuery
                ? true
                : expandedCategories.includes(categoryIndex);
              return (
                <FAQCategory key={categoryIndex}>
                  <CategoryToggle
                    type="button"
                    $expanded={isExpanded}
                    onClick={() => toggleCategory(categoryIndex)}
                    aria-expanded={isExpanded}
                    aria-controls={`faq-panel-${categoryIndex}`}
                    id={`faq-toggle-${categoryIndex}`}
                    disabled={Boolean(normalizedQuery)}
                  >
                    <CategoryChevron $expanded={isExpanded} aria-hidden="true">▶</CategoryChevron>
                    <CategoryLabel>{category.title}</CategoryLabel>
                    <CategoryCount>{category.questions.length}</CategoryCount>
                  </CategoryToggle>
                  {isExpanded && (
                    <FAQQuestionList
                      id={`faq-panel-${categoryIndex}`}
                      role="region"
                      aria-labelledby={`faq-toggle-${categoryIndex}`}
                    >
                      {category.questions.map((question, questionIndex) => (
                        <FAQItem
                          key={questionIndex}
                          type="button"
                          onClick={() => handleFAQClick(question)}
                        >
                          {question}
                        </FAQItem>
                      ))}
                    </FAQQuestionList>
                  )}
                </FAQCategory>
              );
            })}
          </FAQList>
        </FAQSection>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};
